import { SLICE_TYPE } from '@/utils/constants';
import type {
    SiteWideJackpotData,
    SiteWideJackpotGameWindowParamsPrimary,
    SiteWideJackpotMultiplierBoostItem,
    SiteWideJackpotMultiplierBoostPrimary
} from '@/types/aggregation/site-wide-jackpot';
import type { PrismicSlice } from '@/types/prismic/common';
import type { FlattenedSiteWideJackpot, SwjMultiplier } from '@/types/gfe/category';

export type MultiplierFormat = {
    multiplier: number;
    wageredAmount: number;
    oneLiner: string;
    userState: string;
    wagerLabel: string;
    shortWagerLabel: string;
    tickerPillLobbyText?: string;
    extraInfo: {
        text: string;
        spans: { start: number; end: number; type: string }[];
    };
};

export const mapMultipliers = (multipliers: SiteWideJackpotMultiplierBoostItem[]): MultiplierFormat[] =>
    multipliers.map((value) => ({
        multiplier: value.multiplier,
        wageredAmount: value.wagered_amount,
        oneLiner: value.one_liner_title?.[0]?.text ?? '',
        userState: value.user_state?.[0]?.text ?? '',
        wagerLabel: value.wager_label?.[0]?.text ?? '',
        shortWagerLabel: value.short_wager_label?.[0]?.text ?? '',
        tickerPillLobbyText: value.ticker_pill_lobby_text?.[0]?.text ?? '',
        extraInfo: {
            text: value.extra_info[0]?.text ?? '',
            spans: [
                {
                    start: value.extra_info[0]?.spans?.[0]?.start ?? 0,
                    end: value.extra_info[0]?.spans?.[0]?.end ?? 0,
                    type: value.extra_info[0]?.spans?.[0]?.type ?? ''
                }
            ]
        }
    }));

export const mapFlatMultipliers = (multipliers: SwjMultiplier[]): MultiplierFormat[] =>
    multipliers.map((value) => ({
        multiplier: value.multiplier,
        wageredAmount: value.wageredAmount,
        oneLiner: value.oneLinerTitle,
        userState: value.userState,
        wagerLabel: value.wagerLabel,
        shortWagerLabel: value.shortWagerLabel,
        extraInfo: value.extraInfo
    }));

export const rawToFlatMultipliers = (items: SiteWideJackpotMultiplierBoostItem[]): SwjMultiplier[] =>
    items.map((item) => ({
        multiplier: item.multiplier,
        wageredAmount: item.wagered_amount,
        shortWagerLabel: item.short_wager_label?.[0]?.text ?? '',
        wagerLabel: item.wager_label?.[0]?.text ?? '',
        userState: item.user_state?.[0]?.text ?? '',
        oneLinerTitle: item.one_liner_title?.[0]?.text ?? '',
        extraInfo: {
            text: item.extra_info?.[0]?.text ?? '',
            spans: (item.extra_info?.[0]?.spans ?? []).map((span) => ({
                start: span.start,
                end: span.end,
                type: span.type
            }))
        }
    }));

export const mapSWJInfo = (prismicData?: SiteWideJackpotData) => {
    const dybSlice = prismicData?.body?.find(
        (slice) => slice.slice_type === SLICE_TYPE.SITE_WIDE_JACKPOT_MULTIPLIERS
    ) as
        | PrismicSlice<
              'jackpot_multiplier_boost',
              SiteWideJackpotMultiplierBoostItem[],
              SiteWideJackpotMultiplierBoostPrimary
          >
        | undefined;
    return {
        enabled: prismicData?.enabled || false,
        oneLiner: prismicData?.one_liner?.[0]?.text,
        tiers: prismicData?.tiers || [],
        introTitle: prismicData?.title_subtitle?.[0]?.text,
        subHeadline: prismicData?.title_subtitle?.[1]?.text,
        tcShortVersion: prismicData?.tc_short_version?.[0]?.text,
        optedInText: prismicData?.opted_in_text?.[0]?.text,
        optedOutText: prismicData?.opted_out_text?.[0]?.text,
        games: prismicData?.promoted_games || [],
        description: prismicData?.description?.map((desc) => desc.text) || [],
        logo: prismicData?.logo?.url,
        alternateLogo: prismicData?.alternate_logo?.url,
        animatedLogo: prismicData?.animated_logo?.url,
        pageBackground: prismicData?.page_background?.url,
        providerJackpotId: prismicData?.provider_jackpot_id?.[0]?.text,
        providerUid: prismicData?.provider?.uid,
        rulesPageUrl: prismicData?.rules_page_url?.url,
        tierAnimationBackground: prismicData?.tier_animation_background?.url,
        rulesPath: prismicData?.rules_path?.url,
        openRulesExternally: prismicData?.open_rules_externally_android_lobby,
        dybShortRules: dybSlice?.primary?.short_rules?.[0]?.text,
        multipliers: dybSlice?.items || []
    };
};

export const flattenSiteWideJackpot = (swjData?: SiteWideJackpotData): FlattenedSiteWideJackpot => {
    const info = mapSWJInfo(swjData);
    // Mirror GCM's flatten (lambda/shared/transformer/jackpot.ts): collapsedOptedOutText
    // is sourced from the game-window slice's in_game_opted_out_text.
    const gameWindowSlice = swjData?.body?.find((slice) => slice.slice_type === SLICE_TYPE.GAME_WINDOW_PARAMS) as
        | PrismicSlice<'game_window_params', never, SiteWideJackpotGameWindowParamsPrimary>
        | undefined;
    return {
        enabled: info.enabled,
        name: swjData?.name?.[0]?.text ?? '',
        providerJackpotId: info.providerJackpotId ?? '',
        providerUid: info.providerUid ?? '',
        oneLiner: info.oneLiner ?? '',
        introTitle: info.introTitle ?? '',
        subHeadline: info.subHeadline ?? '',
        logo: info.logo ?? '',
        animatedLogo: info.animatedLogo ?? '',
        alternateLogo: info.alternateLogo ?? '',
        rulesPath: info.rulesPath ?? '',
        openRulesExternally: info.openRulesExternally ?? false,
        dybShortRules: info.dybShortRules ?? '',
        multipliers: rawToFlatMultipliers(info.multipliers),
        collapsedOptedOutText: gameWindowSlice?.primary?.in_game_opted_out_text?.[0]?.text ?? '',
        tiers: info.tiers.map((tier) => ({
            tierId: tier.tier_id,
            tierName: tier.tier_name?.[0]?.text ?? '',
            shortTierName: tier.short_tier_name?.[0]?.text ?? '',
            logo1: tier.logo1?.url ?? '',
            minSeed: tier.min_seed,
            odds: tier.odds
        }))
    };
};
